import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  Bell,
  Palette,
  Shield,
  Database,
  Users,
  Mail,
  Smartphone,
  Monitor,
  Moon,
  Sun,
  Check,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Loader2,
  Download,
  Trash2,
  FileText,
  Search,
  Filter,
  RefreshCw,
  LogIn,
  LogOut,
  AlertTriangle,
  Settings2,
  Clock,
  Activity,
  ExternalLink,
  Calendar,
  X,
} from 'lucide-react';
import { useAuth, ROLES } from '../context/AuthContext';
import { useActivityLog, LOG_TYPES, LOG_CATEGORIES } from '../context/ActivityLogContext';
import { useTranslation } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';

const isElectron = () => typeof window !== 'undefined' && window.electronAPI !== undefined;

const Settings = () => {
  const { user, hasRole } = useAuth();
  const { logs, filterLogs, getLogStats, exportLogs, clearLogs } = useActivityLog();
  const { language, setLanguage, availableLanguages, t } = useTranslation();

  const [activeSection, setActiveSection] = useState('notifications');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [settingsId, setSettingsId] = useState(null);


  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    pushAlerts: true,
    breachAlerts: true,
    warningAlerts: true,
    reportReminders: true,
    weeklyDigest: false,
    marketingEmails: false,
  });

  const [appearance, setAppearance] = useState({
    theme: 'light',
    language: 'fr',
    dateFormat: 'DD/MM/YYYY',
    numberFormat: 'fr-FR',
    compactMode: false,
  });

  const [privacy, setPrivacy] = useState({
    profileVisibility: 'team',
    activityStatus: true,
    shareAnalytics: true,
  });


  const loadUserSettings = useCallback(async () => {
    if (isElectron() || !user?.id) {
      setIsLoadingSettings(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();


      if (error && error.code !== 'PGRST116' && error.message?.indexOf('406') === -1) {
        console.error('Error loading user settings:', error);
      }

      if (data) {
        setSettingsId(data.id);
        setNotifications({
          emailAlerts: data.email_notifications ?? true,
          pushAlerts: data.push_notifications ?? true,
          breachAlerts: data.breach_alerts ?? true,
          warningAlerts: data.warning_alerts ?? true,
          reportReminders: data.reporting_reminders ?? true,
          weeklyDigest: data.weekly_digest ?? false,
          marketingEmails: false,
        });
        setAppearance({
          theme: data.theme || 'light',
          language: data.language || 'fr',
          dateFormat: data.date_format || 'DD/MM/YYYY',
          numberFormat: data.number_format || 'fr-FR',
          compactMode: data.compact_mode ?? false,
        });
      }
    } catch (err) {

      console.log('Settings not available:', err.message);
    } finally {
      setIsLoadingSettings(false);
    }
  }, [user?.id]);


  useEffect(() => {
    loadUserSettings();
  }, [loadUserSettings]);


  const [logFilters, setLogFilters] = useState({
    type: '',
    category: '',
    search: '',
    startDate: '',
    endDate: '',
  });
  const [showLogDetails, setShowLogDetails] = useState(null);

  const handleNotificationChange = (key) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAppearanceChange = (key, value) => {
    setAppearance((prev) => ({ ...prev, [key]: value }));
  };

  const handlePrivacyChange = (key, value) => {
    setPrivacy((prev) => ({ ...prev, [key]: typeof value === 'boolean' ? value : value }));
  };

  const handleSave = async () => {
    setIsLoading(true);

    try {
      if (!isElectron() && user?.id) {
        const settingsData = {
          user_id: user.id,
          theme: appearance.theme,
          language: appearance.language,
          date_format: appearance.dateFormat,
          number_format: appearance.numberFormat,
          compact_mode: appearance.compactMode,
          email_notifications: notifications.emailAlerts,
          push_notifications: notifications.pushAlerts,
          breach_alerts: notifications.breachAlerts,
          warning_alerts: notifications.warningAlerts,
          reporting_reminders: notifications.reportReminders,
          weekly_digest: notifications.weeklyDigest,
          updated_at: new Date().toISOString(),
        };

        let error;
        if (settingsId) {

          const result = await supabase
            .from('user_settings')
            .update(settingsData)
            .eq('id', settingsId);
          error = result.error;
        } else {

          const result = await supabase
            .from('user_settings')
            .insert([settingsData])
            .select()
            .single();
          error = result.error;
          if (result.data) {
            setSettingsId(result.data.id);
          }
        }

        if (error) {
          console.error('Error saving settings:', error);
          setMessage({ type: 'error', text: t('settings.saveError') || 'Error saving settings' });
          return;
        }
      }

      setMessage({ type: 'success', text: t('settings.savedSuccessfully') });
    } catch (err) {
      console.error('Error saving settings:', err);
      setMessage({ type: 'error', text: t('settings.saveError') || 'Error saving settings' });
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleExportData = async () => {
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsLoading(false);
    setMessage({ type: 'success', text: 'Export des données en cours de préparation...' });
  };

  const handleExportLogs = (format) => {
    const data = exportLogs(format, logFilters);
    const blob = new Blob([data], {
      type: format === 'json' ? 'application/json' : 'text/csv',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
    setMessage({ type: 'success', text: `Logs exportés en ${format.toUpperCase()}` });
  };

  const handleClearOldLogs = async () => {
    if (!window.confirm('Supprimer les logs de plus de 30 jours ?')) return;
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    clearLogs(thirtyDaysAgo);
    setMessage({ type: 'success', text: 'Anciens logs supprimés' });
  };

  const sections = [
    { id: 'notifications', label: t('settings.notifications'), icon: Bell },
    { id: 'appearance', label: t('settings.appearance'), icon: Palette },
    { id: 'privacy', label: t('settings.privacy'), icon: Shield },
    { id: 'data', label: t('settings.data'), icon: Database },
    ...(hasRole(ROLES.AGENT) ? [{ id: 'logs', label: t('settings.activityLog'), icon: Activity }] : []),
    ...(hasRole(ROLES.AGENT) ? [{ id: 'team', label: t('nav.team'), icon: Users }] : []),
  ];

  const Toggle = ({ checked, onChange }) => (
    <button
      onClick={onChange}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        checked ? 'bg-blue-600' : 'bg-slate-200'
      }`}
    >
      <div
        className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
          checked ? 'translate-x-7' : 'translate-x-1'
        }`}
      />
    </button>
  );

  const getLogTypeInfo = (type) => {
    const types = {
      [LOG_TYPES.CONNECTION]: { label: 'Connexion', icon: LogIn, color: 'text-blue-600 bg-blue-100' },
      [LOG_TYPES.ACTION]: { label: 'Action', icon: Activity, color: 'text-green-600 bg-green-100' },
      [LOG_TYPES.ERROR]: { label: 'Erreur', icon: AlertTriangle, color: 'text-red-600 bg-red-100' },
      [LOG_TYPES.SESSION]: { label: 'Session', icon: Clock, color: 'text-amber-600 bg-amber-100' },
      [LOG_TYPES.CONFIG]: { label: 'Config', icon: Settings2, color: 'text-purple-600 bg-purple-100' },
      [LOG_TYPES.SECURITY]: { label: 'Sécurité', icon: Shield, color: 'text-indigo-600 bg-indigo-100' },
    };
    return types[type] || types[LOG_TYPES.ACTION];
  };

  const formatLogDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;

    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredLogs = filterLogs(logFilters);
  const logStats = getLogStats();

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto">
        {}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <SettingsIcon className="w-7 h-7 text-blue-600" />
            {t('settings.title')}
          </h1>
          <p className="text-slate-500 mt-1">
            {t('settings.managePreferences')}
          </p>
        </div>

        {}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {}
          <div className="lg:w-64 flex-shrink-0">
            <nav className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                      : 'text-slate-600 hover:bg-slate-50 border-l-4 border-transparent'
                  }`}
                >
                  <section.icon className="w-5 h-5" />
                  <span className="font-medium">{section.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {}
          <div className="flex-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              {}
              {activeSection === 'notifications' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">
                    {t('settings.notificationPreferences')}
                  </h2>

                  <div className="space-y-6">
                    {}
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">
                        {t('settings.yourPreferences')}
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Mail className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-800">{t('settings.emailNotifications')}</p>
                              <p className="text-sm text-slate-500">{t('settings.emailNotificationsDesc')}</p>
                            </div>
                          </div>
                          <Toggle
                            checked={notifications.emailAlerts}
                            onChange={() => handleNotificationChange('emailAlerts')}
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Smartphone className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-800">{t('settings.pushNotifications')}</p>
                              <p className="text-sm text-slate-500">
                                {t('settings.pushNotificationsDesc')}
                              </p>
                            </div>
                          </div>
                          <Toggle
                            checked={notifications.pushAlerts}
                            onChange={() => handleNotificationChange('pushAlerts')}
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-800">{t('settings.breachAlerts')}</p>
                            <p className="text-sm text-slate-500">
                              {t('settings.breachAlertsDesc')}
                            </p>
                          </div>
                          <Toggle
                            checked={notifications.breachAlerts}
                            onChange={() => handleNotificationChange('breachAlerts')}
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-800">{t('settings.warningAlerts')}</p>
                            <p className="text-sm text-slate-500">
                              {t('settings.warningAlertsDesc')}
                            </p>
                          </div>
                          <Toggle
                            checked={notifications.warningAlerts}
                            onChange={() => handleNotificationChange('warningAlerts')}
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-800">{t('settings.weeklyDigest')}</p>
                            <p className="text-sm text-slate-500">
                              {t('settings.weeklyDigestDesc')}
                            </p>
                          </div>
                          <Toggle
                            checked={notifications.weeklyDigest}
                            onChange={() => handleNotificationChange('weeklyDigest')}
                          />
                        </div>
                      </div>
                    </div>

                    {}
                    {hasRole(ROLES.AGENT) && (
                      <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Bell className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-blue-800 mb-1">
                              {t('settings.globalNotificationConfig')}
                            </h3>
                            <p className="text-blue-600 text-sm mb-4">
                              {t('settings.globalNotificationConfigDesc')}
                            </p>
                            <Link
                              to="/notifications"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                            >
                              <Bell className="w-4 h-4" />
                              {t('settings.configureNotifications')}
                              <ExternalLink className="w-4 h-4" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {}
              {activeSection === 'appearance' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">
                    {t('settings.appearanceDisplay')}
                  </h2>

                  <div className="space-y-6">
                    {}
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">
                        {t('settings.theme')}
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        {[
                          { id: 'light', label: t('settings.lightTheme'), icon: Sun },
                          { id: 'dark', label: t('settings.darkTheme'), icon: Moon },
                          { id: 'system', label: t('settings.systemTheme'), icon: Monitor },
                        ].map((theme) => (
                          <button
                            key={theme.id}
                            onClick={() => handleAppearanceChange('theme', theme.id)}
                            className={`p-4 rounded-lg border-2 transition-all ${
                              appearance.theme === theme.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <theme.icon
                              className={`w-6 h-6 mx-auto mb-2 ${
                                appearance.theme === theme.id ? 'text-blue-600' : 'text-slate-400'
                              }`}
                            />
                            <p
                              className={`text-sm font-medium ${
                                appearance.theme === theme.id ? 'text-blue-600' : 'text-slate-600'
                              }`}
                            >
                              {theme.label}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {}
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">
                        {t('settings.language')}
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t('settings.selectLanguage')}
                          </label>
                          <div className="space-y-2">
                            {availableLanguages.map((lang) => (
                              <button
                                key={lang.code}
                                onClick={() => setLanguage(lang.code)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                                  language === lang.code
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                <span className="text-2xl">{lang.flag}</span>
                                <div className="flex-1 text-left">
                                  <p className={`font-medium ${language === lang.code ? 'text-blue-600' : 'text-slate-800'}`}>
                                    {lang.nativeName}
                                  </p>
                                  <p className="text-sm text-slate-500">{lang.name}</p>
                                </div>
                                {language === lang.code && (
                                  <Check className="w-5 h-5 text-blue-600" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t('settings.dateFormat')}
                          </label>
                          <select
                            value={appearance.dateFormat}
                            onChange={(e) => handleAppearanceChange('dateFormat', e.target.value)}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {t('settings.numberFormat')}
                          </label>
                          <select
                            value={appearance.numberFormat}
                            onChange={(e) => handleAppearanceChange('numberFormat', e.target.value)}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="fr-FR">1 234 567,89 (FR)</option>
                            <option value="en-US">1,234,567.89 (US)</option>
                            <option value="de-DE">1.234.567,89 (DE)</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {}
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">
                        {t('settings.display')}
                      </h3>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-800">{t('settings.compactMode')}</p>
                          <p className="text-sm text-slate-500">
                            {t('settings.compactModeDesc')}
                          </p>
                        </div>
                        <Toggle
                          checked={appearance.compactMode}
                          onChange={() =>
                            handleAppearanceChange('compactMode', !appearance.compactMode)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {}
              {activeSection === 'privacy' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">
                    {t('settings.privacySecurity')}
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">
                        {t('settings.profileVisibility')}
                      </h3>
                      <div className="space-y-3">
                        {[
                          { value: 'public', label: t('settings.public'), desc: t('settings.publicDesc') },
                          { value: 'team', label: t('settings.teamOnly'), desc: t('settings.teamOnlyDesc') },
                          { value: 'private', label: t('settings.private'), desc: t('settings.privateDesc') },
                        ].map((option) => (
                          <label
                            key={option.value}
                            className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              privacy.profileVisibility === option.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="profileVisibility"
                              value={option.value}
                              checked={privacy.profileVisibility === option.value}
                              onChange={(e) =>
                                handlePrivacyChange('profileVisibility', e.target.value)
                              }
                              className="sr-only"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-slate-800">{option.label}</p>
                              <p className="text-sm text-slate-500">{option.desc}</p>
                            </div>
                            {privacy.profileVisibility === option.value && (
                              <Check className="w-5 h-5 text-blue-600" />
                            )}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">
                        {t('settings.otherOptions')}
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-800">{t('settings.showActivityStatus')}</p>
                            <p className="text-sm text-slate-500">
                              {t('settings.showActivityStatusDesc')}
                            </p>
                          </div>
                          <Toggle
                            checked={privacy.activityStatus}
                            onChange={() =>
                              handlePrivacyChange('activityStatus', !privacy.activityStatus)
                            }
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-800">{t('settings.shareAnalytics')}</p>
                            <p className="text-sm text-slate-500">
                              {t('settings.shareAnalyticsDesc')}
                            </p>
                          </div>
                          <Toggle
                            checked={privacy.shareAnalytics}
                            onChange={() =>
                              handlePrivacyChange('shareAnalytics', !privacy.shareAnalytics)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {}
              {activeSection === 'data' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">
                    {t('settings.dataManagement')}
                  </h2>

                  <div className="space-y-6">
                    {}
                    <div className="p-6 bg-slate-50 rounded-lg">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Download className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-800 mb-1">{t('settings.exportData')}</h3>
                          <p className="text-slate-500 text-sm mb-4">
                            {t('settings.exportDataDesc')}
                          </p>
                          <button
                            onClick={handleExportData}
                            disabled={isLoading}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                          >
                            {isLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                            {t('settings.exportDataButton')}
                          </button>
                        </div>
                      </div>
                    </div>

                    {}
                    <div className="p-6 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                          <Trash2 className="w-6 h-6 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-red-800 mb-1">{t('settings.deleteAccount')}</h3>
                          <p className="text-red-600 text-sm mb-4">
                            {t('settings.deleteAccountDesc')}
                          </p>
                          <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2">
                            <Trash2 className="w-4 h-4" />
                            {t('settings.deleteAccountButton')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {}
              {activeSection === 'logs' && hasRole(ROLES.AGENT) && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-800">{t('settings.activityLog')}</h2>
                      <p className="text-sm text-slate-500">
                        {t('settings.activityLogDesc')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleExportLogs('csv')}
                        className="px-3 py-1.5 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 flex items-center gap-1"
                      >
                        <Download className="w-4 h-4" />
                        CSV
                      </button>
                      <button
                        onClick={() => handleExportLogs('json')}
                        className="px-3 py-1.5 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 flex items-center gap-1"
                      >
                        <Download className="w-4 h-4" />
                        JSON
                      </button>
                    </div>
                  </div>

                  {}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-slate-800">{logStats.total}</p>
                      <p className="text-sm text-slate-500">Total</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{logStats.last24h}</p>
                      <p className="text-sm text-slate-500">Dernières 24h</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{logStats.byType.connection}</p>
                      <p className="text-sm text-slate-500">Connexions</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-600">{logStats.errors24h}</p>
                      <p className="text-sm text-slate-500">Erreurs (24h)</p>
                    </div>
                  </div>

                  {}
                  <div className="flex flex-wrap gap-3 mb-4 p-4 bg-slate-50 rounded-lg">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={logFilters.search}
                        onChange={(e) => setLogFilters((prev) => ({ ...prev, search: e.target.value }))}
                        placeholder="Rechercher..."
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <select
                      value={logFilters.type}
                      onChange={(e) => setLogFilters((prev) => ({ ...prev, type: e.target.value }))}
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Tous les types</option>
                      <option value={LOG_TYPES.CONNECTION}>Connexions</option>
                      <option value={LOG_TYPES.ACTION}>Actions</option>
                      <option value={LOG_TYPES.ERROR}>Erreurs</option>
                      <option value={LOG_TYPES.SESSION}>Sessions</option>
                      <option value={LOG_TYPES.CONFIG}>Configuration</option>
                      <option value={LOG_TYPES.SECURITY}>Sécurité</option>
                    </select>

                    <select
                      value={logFilters.category}
                      onChange={(e) => setLogFilters((prev) => ({ ...prev, category: e.target.value }))}
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Toutes catégories</option>
                      <option value={LOG_CATEGORIES.AUTH}>Authentification</option>
                      <option value={LOG_CATEGORIES.USER}>Utilisateurs</option>
                      <option value={LOG_CATEGORIES.LOAN}>Prêts</option>
                      <option value={LOG_CATEGORIES.TEAM}>Équipe</option>
                      <option value={LOG_CATEGORIES.SETTINGS}>Paramètres</option>
                    </select>

                    {hasRole(ROLES.AGENT) && (
                      <button
                        onClick={handleClearOldLogs}
                        className="px-3 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Purger
                      </button>
                    )}
                  </div>

                  {}
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                      {filteredLogs.slice(0, 50).map((log) => {
                        const typeInfo = getLogTypeInfo(log.type);
                        const TypeIcon = typeInfo.icon;

                        return (
                          <div
                            key={log.id}
                            className="flex items-start gap-3 p-4 border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                            onClick={() => setShowLogDetails(log)}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${typeInfo.color}`}>
                              <TypeIcon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800 truncate">{log.message}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-slate-500">{log.userName || log.user_id || 'Système'}</span>
                                <span className="text-xs text-slate-300">•</span>
                                <span className="text-xs text-slate-500">{formatLogDate(log.timestamp || log.created_at)}</span>
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        );
                      })}

                      {filteredLogs.length === 0 && (
                        <div className="p-8 text-center">
                          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500">Aucun log trouvé</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {filteredLogs.length > 50 && (
                    <p className="text-center text-sm text-slate-500 mt-4">
                      Affichage des 50 premiers résultats sur {filteredLogs.length}
                    </p>
                  )}
                </div>
              )}

              {}
              {activeSection === 'team' && hasRole(ROLES.AGENT) && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-800">{t('settings.teamManagement')}</h2>
                      <p className="text-sm text-slate-500">
                        {t('settings.teamManagementDesc')}
                      </p>
                    </div>
                    <Link
                      to="/team"
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Users className="w-4 h-4" />
                      {t('settings.manageTeam')}
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>

                  <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-blue-800 mb-1">
                          {t('settings.accessFullManagement')}
                        </h3>
                        <p className="text-blue-600 text-sm mb-4">
                          {t('settings.accessFullManagementDesc')}
                        </p>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• {t('settings.teamFeatures.invite')}</li>
                          <li>• {t('settings.teamFeatures.roles')}</li>
                          <li>• {t('settings.teamFeatures.invitations')}</li>
                          <li>• {t('settings.teamFeatures.activity')}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {}
              {activeSection !== 'logs' && activeSection !== 'team' && (
                <div className="mt-8 pt-6 border-t border-slate-200 flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={isLoading}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('settings.saving')}
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        {t('settings.saveChanges')}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {}
      {showLogDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">Détails du log</h2>
                <button
                  onClick={() => setShowLogDetails(null)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                {(() => {
                  const typeInfo = getLogTypeInfo(showLogDetails.type);
                  const TypeIcon = typeInfo.icon;
                  return (
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeInfo.color}`}>
                      <TypeIcon className="w-5 h-5" />
                    </div>
                  );
                })()}
                <div>
                  <p className="font-semibold text-slate-800">{showLogDetails.message}</p>
                  <p className="text-sm text-slate-500">
                    {new Date(showLogDetails.timestamp || showLogDetails.created_at).toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Utilisateur</p>
                  <p className="font-medium text-slate-800">{showLogDetails.userName || showLogDetails.user_id || 'Système'}</p>
                  <p className="text-sm text-slate-500">{showLogDetails.userEmail || ''}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Type</p>
                  <p className="font-medium text-slate-800">{getLogTypeInfo(showLogDetails.type).label}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Catégorie</p>
                  <p className="font-medium text-slate-800">{showLogDetails.category}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Action</p>
                  <p className="font-medium text-slate-800">{showLogDetails.action}</p>
                </div>
                {showLogDetails.ipAddress && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Adresse IP</p>
                    <p className="font-medium text-slate-800">{showLogDetails.ipAddress}</p>
                  </div>
                )}
              </div>

              {showLogDetails.details && Object.keys(showLogDetails.details).length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Détails</p>
                  <pre className="p-3 bg-slate-100 rounded-lg text-sm text-slate-700 overflow-x-auto">
                    {JSON.stringify(showLogDetails.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;